/* eslint-disable */
import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import '../styles/Popular.css';
import '../styles/Recipe.css';
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { motion } from "framer-motion";
import { groupIngredientsByCategory } from '../utils/ingredientUtils';
import { WeeklyContextMenu } from '../components/WeeklyContextMenu';
import { useData } from '../contexts/DataContext';

// ─────────────────────────────────────────────────────────────────────────────
// INGREDIENT SCALING UTILITIES
// ─────────────────────────────────────────────────────────────────────────────

const fractionToDecimal = (str) => {
    if (str.includes('/')) {
        const [num, den] = str.split('/').map(Number);
        return num / den;
    }
    return parseFloat(str);
};

const decimalToFraction = (decimal) => {
    if (decimal === 0) return '0';
    const tolerance = 0.02;
    const commonFractions = [[1,8],[1,4],[1,3],[1,2],[2,3],[3,4],[7,8]];
    const whole = Math.floor(decimal);
    const remainder = decimal - whole;
    if (remainder < tolerance) return whole === 0 ? '0' : String(whole);
    if (remainder > 1 - tolerance) return String(whole + 1);
    let bestFrac = null, bestDiff = Infinity;
    for (const [num, den] of commonFractions) {
        const diff = Math.abs(remainder - num / den);
        if (diff < bestDiff) { bestDiff = diff; bestFrac = [num, den]; }
    }
    if (bestDiff < tolerance && bestFrac) {
        const fracStr = `${bestFrac[0]}/${bestFrac[1]}`;
        return whole === 0 ? fracStr : `${whole} ${fracStr}`;
    }
    return String(parseFloat(decimal.toFixed(2)));
};

const scaleIngredient = (original, scaleFactor) => {
    if (scaleFactor === 1) return original;
    const numPattern      = /^(\d+)\s+and\s+(\d+\/\d+)/;
    const mixedPattern    = /^(\d+)\s+(\d+\/\d+)/;
    const fractionPattern = /^(\d+\/\d+)/;
    const rangePattern    = /^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)(?=\s|$)/;
    const wholePattern    = /^(\d+(?:\.\d+)?)/;
    let match;
    match = original.match(numPattern);
    if (match) {
        const value = parseInt(match[1]) + fractionToDecimal(match[2]);
        return decimalToFraction(value * scaleFactor) + original.slice(match[0].length);
    }
    match = original.match(mixedPattern);
    if (match) {
        const value = parseInt(match[1]) + fractionToDecimal(match[2]);
        return decimalToFraction(value * scaleFactor) + original.slice(match[0].length);
    }
    match = original.match(fractionPattern);
    if (match) {
        return decimalToFraction(fractionToDecimal(match[1]) * scaleFactor) + original.slice(match[0].length);
    }
    match = original.match(rangePattern);
    if (match) {
        const lo = parseFloat(match[1]) * scaleFactor;
        const hi = parseFloat(match[2]) * scaleFactor;
        return `${decimalToFraction(lo)}-${decimalToFraction(hi)}${original.slice(match[0].length)}`;
    }
    match = original.match(wholePattern);
    if (match) {
        const rest = original.slice(match[0].length);
        if (['°', '%', '"', "'"].includes(rest[0] || '')) return original;
        return decimalToFraction(parseFloat(match[1]) * scaleFactor) + rest;
    }
    return original;
};

// ─────────────────────────────────────────────────────────────────────────────
// GROCERY LIST MODAL
// ─────────────────────────────────────────────────────────────────────────────
function GroceryListModal({ recipe, scaleFactor, scaledServings, baseServings, onClose }) {
    const [checkedIngredients, setCheckedIngredients] = useState({});
    const toggleIngredient = (text) => setCheckedIngredients(prev => ({ ...prev, [text]: !prev[text] }));

    // Build scaled ingredient list grouped by category
    const ing = recipe.extendedIngredients || [];
    const allScaled = ing[0]?.section !== undefined
        ? ing.flatMap(s => s.ingredients.map(i => scaleIngredient(i.original, scaleFactor)))
        : ing.map(i => scaleIngredient(i.original, scaleFactor));
    const categoryGroups = groupIngredientsByCategory(allScaled);

    // Dismiss on backdrop click or Escape
    const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };
    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const printGroceryList = () => {
        const printWindow = window.open('', '', 'height=800,width=800');
        if (!printWindow) { alert('Please allow popups for this site to use the print feature.'); return; }

        printWindow.document.write('<html><head><title>' + recipe.title + ' - Grocery List</title>');
        printWindow.document.write('<style>');
        printWindow.document.write('body { font-family: Arial, sans-serif; padding: 20px; }');
        printWindow.document.write('h1 { color: #313131; border-bottom: 2px solid #494949; padding-bottom: 10px; }');
        printWindow.document.write('h3 { color: #494949; margin-top: 20px; margin-bottom: 4px; font-size: 14px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }');
        printWindow.document.write('ul { list-style-type: none; padding-left: 0; margin: 0; }');
        printWindow.document.write('li { padding: 7px 0; border-bottom: 1px solid #eee; font-size: 14px; }');
        printWindow.document.write('li.checked { color: #bbb; text-decoration: line-through; }');
        printWindow.document.write('li.checked:before { content: "☑ "; font-size: 16px; margin-right: 8px; }');
        printWindow.document.write('li:not(.checked):before { content: "☐ "; font-size: 16px; margin-right: 8px; }');
        printWindow.document.write('.recipe-info { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }');
        printWindow.document.write('@media print { body { padding: 10px; } }');
        printWindow.document.write('</style></head><body>');

        printWindow.document.write('<h1>' + recipe.title + '</h1>');
        printWindow.document.write('<div class="recipe-info"><p><strong>Servings:</strong> ' + scaledServings +
            (scaleFactor !== 1 ? ' <em>(scaled from ' + baseServings + ')</em>' : '') + '</p></div>');

        categoryGroups.forEach(({ category, emoji, items }) => {
            printWindow.document.write('<h3>' + emoji + ' ' + category + '</h3><ul>');
            items.forEach((item) => {
                const isChecked = !!checkedIngredients[item];
                printWindow.document.write('<li' + (isChecked ? ' class="checked"' : '') + '>' + item + '</li>');
            });
            printWindow.document.write('</ul>');
        });

        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <div onClick={handleBackdropClick} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
        }}>
            <div style={{
                background: 'white', borderRadius: '12px', width: '100%', maxWidth: '520px',
                maxHeight: '90vh', display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                    padding: '1.25rem 1.5rem 1rem', borderBottom: '1px solid #eee', flexShrink: 0,
                }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#313131' }}>
                            🛒 Grocery List
                        </h2>
                        <p style={{ margin: '0.2rem 0 0', fontSize: '0.85rem', color: '#888' }}>
                            {recipe.title} &mdash; {scaledServings} serving{scaledServings !== 1 ? 's' : ''}
                            {scaleFactor !== 1 && <span style={{ color: '#1565C0', fontWeight: 600 }}> (scaled)</span>}
                        </p>
                    </div>
                    <button onClick={onClose} style={{
                        background: 'none', border: 'none', fontSize: '1.8rem',
                        lineHeight: 1, cursor: 'pointer', color: '#999', padding: '0 0.25rem', marginTop: '-0.25rem',
                    }}>×</button>
                </div>

                {/* Ingredient list */}
                <div style={{ overflowY: 'auto', flex: 1, padding: '0.75rem 1.5rem' }}>
                    {categoryGroups.map(({ category, emoji, items }) => (
                        <div key={category} style={{ marginBottom: '1rem' }}>
                            <p style={{
                                margin: '0.5rem 0 0.25rem', fontSize: '0.75rem', fontWeight: 700,
                                color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em',
                                display: 'flex', alignItems: 'center', gap: '4px',
                            }}>
                                <span>{emoji}</span>{category}
                            </p>
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                                {items.map((item, i) => {
                                    const checked = !!checkedIngredients[item];
                                    return (
                                        <li
                                            key={i}
                                            onClick={() => toggleIngredient(item)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '0.6rem',
                                                padding: '0.45rem 0', cursor: 'pointer',
                                                borderBottom: '1px solid #f5f5f5', userSelect: 'none',
                                                color: checked ? '#bbb' : '#313131',
                                                textDecoration: checked ? 'line-through' : 'none',
                                            }}
                                        >
                                            <span style={{ fontSize: '1rem', flexShrink: 0, color: '#494949' }}>
                                                {checked ? '☑' : '☐'}
                                            </span>
                                            <span style={{ fontSize: '0.92rem', lineHeight: 1.4 }}>{item}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex', gap: '0.75rem', padding: '1rem 1.5rem',
                    borderTop: '1px solid #eee', flexShrink: 0, background: '#fafafa',
                }}>
                    <button
                        onClick={printGroceryList}
                        style={{
                            flex: 1, padding: '0.7rem', background: '#4CAF50', color: 'white',
                            border: 'none', borderRadius: '6px', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#45a049'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#4CAF50'}
                    >
                        🖨️ Print Grocery List
                    </button>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '0.7rem 1.25rem', background: 'white', color: '#666',
                            border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem',
                            fontWeight: 600, cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

function Recipe() {
    const { name } = useParams();
    const { state } = useLocation();

    const { recipes, weeklyMeals } = useData();
    const filteredRecipe = recipes.find((r) => r.id === Number(name) || r.id === name);

    const [activeTab,       setActiveTab]       = useState("instructions");
    const [addToPlanMenu,   setAddToPlanMenu]   = useState(null);
    const [showGroceryList, setShowGroceryList] = useState(false);

    const baseServings    = filteredRecipe?.servings ?? 1;
    const initialServings = state?.scaledServings ?? baseServings;
    const [scaledServings, setScaledServings] = useState(initialServings);
    const scaleFactor = scaledServings / baseServings;

    useEffect(() => {
        if (filteredRecipe && !state?.scaledServings) {
            setScaledServings(filteredRecipe.servings);
        }
    }, [filteredRecipe?.id, state?.scaledServings]);

    if (!filteredRecipe) {
        return (
            <div style={{ padding: '4rem', textAlign: 'center', color: '#888' }}>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
                Loading recipe…
            </div>
        );
    }

    const handleAddToPlanClick = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setAddToPlanMenu({ x: rect.left, y: rect.bottom + 6 });
    };

    // ── Print: Instructions ────────────────────────────────────────────────
    const printInstructions = () => {
        const printWindow = window.open('', '', 'height=800,width=800');
        if (!printWindow) { alert('Please allow popups for this site to use the print feature.'); return; }

        setTimeout(() => {
            printWindow.document.write('<html><head><title>' + filteredRecipe.title + ' - Instructions</title>');
            printWindow.document.write('<style>');
            printWindow.document.write('body { font-family: Arial, sans-serif; padding: 20px; }');
            printWindow.document.write('h1 { color: #313131; border-bottom: 2px solid #494949; padding-bottom: 10px; }');
            printWindow.document.write('h2 { color: #494949; margin-top: 20px; }');
            printWindow.document.write('h3 { color: #494949; margin-top: 16px; border-bottom: 1px solid #ccc; padding-bottom: 6px; }');
            printWindow.document.write('p { margin: 5px 0; } ol { line-height: 2; } li { margin-bottom: 15px; }');
            printWindow.document.write('img { max-width: 300px; margin: 10px 0; display: block; }');
            printWindow.document.write('.recipe-info { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }');
            printWindow.document.write('.equipment-images { display: flex; flex-wrap: wrap; gap: 10px; margin: 10px 0; }');
            printWindow.document.write('.equipment-images img { max-width: 200px; }');
            printWindow.document.write('@media print { body { padding: 10px; } }');
            printWindow.document.write('</style></head><body>');

            printWindow.document.write('<h1>' + filteredRecipe.title + '</h1>');
            printWindow.document.write('<div class="recipe-info">');
            printWindow.document.write('<p><strong>Category:</strong> ' + (filteredRecipe.cuisines || []).join(', ') + '</p>');
            printWindow.document.write('<p><strong>Dish Type:</strong> ' + (filteredRecipe.dishTypes || []).join(', ') + '</p>');
            printWindow.document.write('<p><strong>Ready in:</strong> ' + filteredRecipe.readyInMinutes + ' minutes</p>');
            printWindow.document.write('<p><strong>Servings:</strong> ' + scaledServings +
                (scaleFactor !== 1 ? ' <em>(scaled from ' + baseServings + ')</em>' : '') + '</p>');
            printWindow.document.write('</div>');

            if (filteredRecipe.equipment && filteredRecipe.equipment.length > 0) {
                printWindow.document.write('<h2>Equipment</h2>');
                filteredRecipe.equipment.forEach((item) => {
                    if (item.items) printWindow.document.write('<p>' + item.items + '</p>');
                    if (item.images && item.images.length > 0) {
                        printWindow.document.write('<div class="equipment-images">');
                        item.images.forEach((img) => printWindow.document.write('<img src="' + img.url + '" alt="' + img.alt + '">'));
                        printWindow.document.write('</div>');
                    }
                });
            }

            printWindow.document.write('<h2>Instructions</h2>');
            const printSteps = (steps) => {
                printWindow.document.write('<ol>');
                (steps || []).forEach((step) => {
                    printWindow.document.write('<li>' + step.step);
                    if (step.images && step.images.length > 0) {
                        step.images.forEach((img) => printWindow.document.write('<br><img src="' + img.url + '" alt="' + img.alt + '">'));
                    }
                    printWindow.document.write('</li>');
                });
                printWindow.document.write('</ol>');
            };

            (filteredRecipe.analyzedInstructions || []).forEach((section) => {
                printWindow.document.write('<h3>' + section.section + '</h3>');
                if (section.note) printWindow.document.write('<p><em>' + section.note + '</em></p>');
                printSteps(section.steps);
            });

            printWindow.document.write('</body></html>');
            printWindow.document.close();

            const handleLoad = () => {
                const imgs = Array.from(printWindow.document.images);
                if (imgs.length === 0) { setTimeout(() => printWindow.print(), 100); return; }
                let loaded = imgs.filter(i => i.complete).length;
                if (loaded === imgs.length) { setTimeout(() => printWindow.print(), 100); return; }
                const check = () => { loaded++; if (loaded === imgs.length) setTimeout(() => printWindow.print(), 100); };
                imgs.forEach(i => { if (!i.complete) { i.onload = check; i.onerror = check; } });
                setTimeout(() => printWindow.print(), 3000);
            };

            if (printWindow.document.readyState === 'complete') handleLoad();
            else printWindow.onload = handleLoad;
        }, 50);
    };

    // ── Step list renderer ─────────────────────────────────────────────────
    const renderStepList = (steps) => (
        <ol>
            {(steps || []).map((step, index) => (
                <li key={index}>
                    {step.step}
                    {step.images && step.images.length > 0 && (
                        <div className="step-images">
                            {step.images.map((image, imgIndex) => (
                                <img key={imgIndex} src={image.url} alt={image.alt} />
                            ))}
                        </div>
                    )}
                </li>
            ))}
        </ol>
    );

    // ── Servings scaler UI ─────────────────────────────────────────────────
    const ServingsScaler = () => (
        <div className="scaler-wrapper">
            <span className="scaler-label">Servings</span>
            <div className="scaler-controls">
                <button
                    className="scaler-btn"
                    onClick={() => setScaledServings(s => Math.max(1, s - 1))}
                    disabled={scaledServings <= 1}
                    aria-label="Decrease servings"
                >−</button>
                <input
                    className="scaler-input"
                    type="number"
                    min="1"
                    max="100"
                    value={scaledServings}
                    onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (!isNaN(val) && val >= 1 && val <= 100) setScaledServings(val);
                    }}
                    aria-label="Number of servings"
                />
                <button
                    className="scaler-btn"
                    onClick={() => setScaledServings(s => Math.min(100, s + 1))}
                    disabled={scaledServings >= 100}
                    aria-label="Increase servings"
                >+</button>
            </div>
            {scaleFactor !== 1 && (
                <div className="scale-badge">
                    {scaleFactor > 1 ? '×' : '÷'}{' '}
                    {scaleFactor > 1
                        ? parseFloat(scaleFactor.toFixed(2))
                        : parseFloat((1 / scaleFactor).toFixed(2))}
                    <button className="scale-reset-btn" onClick={() => setScaledServings(baseServings)}>
                        Reset
                    </button>
                </div>
            )}
        </div>
    );

    // ── Single ingredient with scaling applied ─────────────────────────────
    const ScaledIngredient = ({ text }) => {
        const scaled = scaleIngredient(text, scaleFactor);
        const changed = scaled !== text;
        return (
            <span className={`ingredient-item${changed ? ' scaled' : ''}`}>
                {scaled}
                {changed && (
                    <span className="original-hint-wrapper">
                        <sup className="original-hint">*</sup>
                        <span className="original-hint-tooltip">Original: {text}</span>
                    </span>
                )}
            </span>
        );
    };

    // ── RENDER ─────────────────────────────────────────────────────────────
    return (
        <motion.div
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            {/* Grocery List Modal */}
            {showGroceryList && (
                <GroceryListModal
                    recipe={filteredRecipe}
                    scaleFactor={scaleFactor}
                    scaledServings={scaledServings}
                    baseServings={baseServings}
                    onClose={() => setShowGroceryList(false)}
                />
            )}

            <div className="detail-wrapper">
                <div>
                    <h2>{filteredRecipe.title}</h2>
                    <img src={filteredRecipe.image} alt={filteredRecipe.title} />
                </div>
                <div className="recipe-info-panel">

                    <div className="tab-row">
                        <button className={`tab-btn${activeTab === "instructions" ? " active" : ""}`} onClick={() => setActiveTab("instructions")}>Instructions</button>
                        <button className={`tab-btn${activeTab === "ingredients"  ? " active" : ""}`} onClick={() => setActiveTab("ingredients")}>Ingredients</button>
                        <button className={`tab-btn${activeTab === "equipment"    ? " active" : ""}`} onClick={() => setActiveTab("equipment")}>Equipment</button>
                    </div>

                    <div className="print-btn-row">
                        <button className="print-btn" onClick={printInstructions}>🖨️ Print Instructions</button>
                        <button className="print-btn" onClick={() => setShowGroceryList(true)}>🛒 Grocery List</button>
                        <button
                            className="print-btn"
                            onClick={handleAddToPlanClick}
                            title="Add this recipe to your weekly meal plan"
                            style={{ background: '#4CAF50', color: 'white', borderColor: '#4CAF50' }}
                        >
                            📅 Add to Plan
                        </button>
                    </div>

                    {/* ── INSTRUCTIONS TAB ── */}
                    {activeTab === "instructions" && (
                        <div>
                            <h4>
                                Category: {(filteredRecipe.cuisines || []).join(', ')}<br />
                                DishType: {(filteredRecipe.dishTypes || []).join(', ')}<br />
                                Ready in: {filteredRecipe.readyInMinutes} minutes.&nbsp;
                                Servings: {scaledServings}
                                {scaleFactor !== 1 && (
                                    <span className="scaled-note"> (scaled from {baseServings})</span>
                                )}
                            </h4>

                            {(filteredRecipe.analyzedInstructions || []).map((section, sIndex) => (
                                <div key={sIndex}>
                                    <h3 className="section-heading">{section.section}</h3>
                                    {section.note && (
                                        <p style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
                                            <em>{section.note}</em>
                                        </p>
                                    )}
                                    {renderStepList(section.steps)}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ── INGREDIENTS TAB ── */}
                    {activeTab === "ingredients" && (
                        <div>
                            <ServingsScaler />
                            {scaleFactor !== 1 && (
                                <p className="scale-hint-text">* Quantities marked with an asterisk have been scaled.</p>
                            )}

                            {(filteredRecipe.extendedIngredients || []).map((section, sIndex) => (
                                <div key={sIndex}>
                                    <h3 className="section-heading">{section.section}</h3>
                                    <ul style={{ listStyleType: "none" }}>
                                        {(section.ingredients || []).map((ingredient) => (
                                            <li key={ingredient.id}>
                                                <ScaledIngredient text={ingredient.original} />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ── EQUIPMENT TAB ── */}
                    {activeTab === "equipment" && (
                        <div>
                            {filteredRecipe.equipment && filteredRecipe.equipment.length > 0 ? (
                                <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3 }}>
                                    <Masonry>
                                        {filteredRecipe.equipment.map((item, index) => (
                                            item.images && item.images.length > 0 && item.images.map((image, imgIndex) => (
                                                <div key={`${index}-${imgIndex}`} className="image-container">
                                                    <img height="100%" width="100%" src={image.url} alt={image.alt}
                                                        style={{ display: "block", padding: "8px", borderRadius: "16px" }} />
                                                    <div className="image-text">{image.alt}</div>
                                                </div>
                                            ))
                                        ))}
                                    </Masonry>
                                </ResponsiveMasonry>
                            ) : (
                                <p>No equipment information available for this recipe.</p>
                            )}
                        </div>
                    )}

                </div>
            </div>

            {/* ── Add to Plan context menu ── */}
            {addToPlanMenu && (
                <WeeklyContextMenu
                    x={addToPlanMenu.x}
                    y={addToPlanMenu.y}
                    recipe={{ id: filteredRecipe.id, title: filteredRecipe.title, servings: baseServings }}
                    weeklyMeals={weeklyMeals}
                    targetServings={scaledServings}
                    onClose={() => setAddToPlanMenu(null)}
                />
            )}

        </motion.div>
    );
}

export default Recipe;
